/**
 * Format a number as currency
 * @param {number} value - The numeric value to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'USD', decimals = 2) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format a number with thousands separators
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (value) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Calculate percentage
 * @param {number} numerator
 * @param {number} denominator
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (numerator, denominator, decimals = 2) => {
  if (denominator === 0 || isNaN(numerator) || isNaN(denominator)) {
    return '0.00%';
  }

  const percentage = (numerator / denominator) * 100;
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Calculate CTR (Click-Through Rate)
 * @param {number} clicks
 * @param {number} impressions
 * @returns {string} Formatted CTR percentage
 */
export const calculateCTR = (clicks, impressions) => {
  return formatPercentage(clicks, impressions);
};

/**
 * Calculate eCPM (Effective Cost Per Mille)
 * @param {number} revenue
 * @param {number} impressions
 * @returns {string} Formatted eCPM value
 */
export const calculateECPM = (revenue, impressions) => {
  if (impressions === 0 || isNaN(revenue) || isNaN(impressions)) {
    return '$0.00';
  }

  const ecpm = (revenue / impressions) * 1000;
  return formatCurrency(ecpm);
};

/**
 * Format an ISO 8601 date-time string (AdUnit.createdAt / updatedAt) for display.
 * @param {string} isoString - ISO 8601 date-time string
 * @returns {string} Human-readable date string
 */
export const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(isoString));
  } catch {
    return '—';
  }
};
